









































































































































import moment from 'moment-timezone'
import * as R from 'ramda'

import { mapActions, mapGetters } from 'vuex'
import { ModuleNames } from '@/store/module-names'
import { GetterNames, ActionNames } from '@/store/modules/exercise-program/keys'
import { GetterNames as PatientGetterNames } from '@/store/modules/patient/keys'
import { GetterNames as SiteStatsGetterNames } from '@/store/modules/site-stats/keys'
import { getPatientName, getPatientInitials, sortByPatientName } from '@/utils/patients'
import { UserActionContexts } from '@/constants/urls'

import ExerciseProgramDatePickerWrapper from '@/components/exercise-programs/forms/ExerciseProgramDatePickerWrapper.vue'

type ComponentData = {
  programDetailsAreValid: boolean
  participantSearchText: string | null
  site: string
  title: string
  description: string
  startDate: string
  endDate: string
  participants: string[]
  [key: string]: any
}

export default {
  components: {
    ExerciseProgramDatePickerWrapper,
  },
  props: {
    showDialog: {
      type: Boolean,
      required: true,
    },
    userActionContext: {
      type: String,
      required: true,
    },
  },
  data(): ComponentData {
    return {
      isSavingProgramData: false,
      participantSearchText: '',
      site: '',
      title: '',
      description: '',
      startDate: moment().format('YYYY-MM-DD'),
      endDate: moment()
        .add(1, 'days')
        .format('YYYY-MM-DD'),
      participants: [],
      programDetailsAreValid: false,
      titleRules: [v => !!v || 'Please enter a program name'],
      participantRules: [v => (v.length ? true : 'Please select at least one participant')],
    }
  },
  computed: {
    userActionContextIsNew() {
      return this.userActionContext === UserActionContexts.New
    },
    userActionContextIsEdit() {
      return this.userActionContext === UserActionContexts.Edit
    },
    potentialParticipants() {
      const sortedPatients = sortByPatientName(this[PatientGetterNames.patients])
      const mappedPatients = sortedPatients
        .filter(patient => {
          return patient.site === this.site && patient.status === 'active'
        })
        .map(patient => {
          return {
            id: patient.id,
            fullName: getPatientName(patient),
            initials: getPatientInitials(patient),
            site: patient.site,
          }
        })
        .filter(patient => {
          if (this.hasParticipantSearchText) {
            return patient.fullName.toLowerCase().includes(this.participantSearchText.toLowerCase())
          } else {
            return true
          }
        })
      return mappedPatients
    },
    allPotentialParticipantsSelected() {
      return this.participants.length === this.potentialParticipants.length
    },
    somePotentialParticipantsSelected() {
      return this.participants.length > 0 && !this.allPotentialParticipantsSelected
    },
    selectAllPotentialParticipantsIcon() {
      if (this.allPotentialParticipantsSelected) {
        return 'mdi-close-box'
      } else if (this.somePotentialParticipantsSelected) {
        return 'mdi-minus-box'
      } else {
        return 'mdi-checkbox-blank-outline'
      }
    },
    hasParticipantSearchText() {
      return this.participantSearchText ? true : false
    },
    sitesInOrg() {
      return this[SiteStatsGetterNames.siteStatses].map(site => ({ id: site.id, siteName: site.siteName }))
    },
    hasSiteStatses() {
      return !R.isEmpty(this.sitesInOrg)
    },
    singleSiteInOrg() {
      return this.hasSiteStatses && R.length(this.sitesInOrg) === 1
    },
    dateRules() {
      return moment(this.endDate).isAfter(this.startDate) ? [] : ['End date must be later than start date']
    },
    programDetails() {
      return {
        site: this.site,
        title: this.title,
        description: this.description,
        startDate: this.startDate,
        endDate: this.endDate,
        participants: this.participants,
      }
    },
    ...mapGetters(ModuleNames.exerciseProgram, {
      chosenExerciseProgram: GetterNames.chosenExerciseProgram,
      chosenExerciseProgramId: GetterNames.chosenExerciseProgramId,
    }),
    ...mapGetters(ModuleNames.patient, [PatientGetterNames.patients]),
    ...mapGetters(ModuleNames.siteStats, [SiteStatsGetterNames.siteStatses]),
  },
  watch: {
    showDialog: {
      handler(newVal, oldVal) {
        if (newVal) {
          if (this.userActionContextIsEdit) {
            this.mapExistingProgramDetails()
          } else if (this.userActionContextIsNew && this.hasSiteStatses && this.singleSiteInOrg) {
            this.site = this.sitesInOrg[0].siteName
          }
        }
      },
    },
  },
  methods: {
    mapExistingProgramDetails() {
      ;['title', 'description', 'startDate', 'endDate', 'site', 'participants'].forEach(prop => {
        this[prop] = this.chosenExerciseProgram[prop]
      })
    },
    async saveProgramDetails() {
      if (!this.programDetailsAreValid) return
      this.isSavingProgramData = true
      if (this.userActionContextIsNew) {
        await this.postNewProgram()
      } else if (this.userActionContextIsEdit) {
        await this.patchProgram()
      }
      this.isSavingProgramData = false
      this.closeDialog()
    },
    async postNewProgram() {
      await this[ActionNames.postExerciseProgram](this.programDetails)
      this.$emit('afterNewProgramPost')
    },
    async patchProgram() {
      await this[ActionNames.patchExerciseProgram]({
        id: this.chosenExerciseProgramId,
        attributes: this.programDetails,
      })
    },
    toggleSelectAllPotentialParticipants() {
      this.$nextTick(() => {
        if (this.allPotentialParticipantsSelected) {
          this.participants = []
        } else {
          this.participants = this.potentialParticipants.slice()
        }
      })
    },
    clearChosenParticipantsOnSiteChange() {
      this.participantSearchText = ''
      this.participants = []
    },
    closeDialog() {
      this.$emit('closeDialog')
      this.$refs.newProgramForm.reset()
    },
    ...mapActions(ModuleNames.exerciseProgram, [ActionNames.postExerciseProgram, ActionNames.patchExerciseProgram]),
  },
}
